// src/pages/Document/DocumentPage.jsx
import React, { useState, useMemo } from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import DocumentListView from "./DocumentListView";
import PageLayout, { PageHeader, StatsGrid, Toolbar } from "../../components/common/PageLayout";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { 
  FileText, X, Download 
} from "lucide-react";

const DocumentPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null); // 🟢 For Split Pane

  // Note: Visual representations of document metrics
  const statsCounts = { total: 124, pending: 18, approved: 95, rejected: 11 };

  const stats = useMemo(() => [
    {
      label: "Total Files",
      value: statsCounts.total,
      icon: DocumentTextIcon,
      variant: "default",
    },
    {
      label: "Pending Review",
      value: statsCounts.pending,
      icon: ClockIcon,
      variant: "orange",
    },
    {
      label: "Approved",
      value: statsCounts.approved,
      icon: CheckCircleIcon,
      variant: "green",
    },
    {
      label: "Rejected / Expired",
      value: statsCounts.rejected,
      icon: ExclamationCircleIcon,
      variant: "red",
    },
  ], [statsCounts]);

  return (
    <PageLayout>
      {/* 1. HEADER */}
      <PageHeader
        icon={DocumentTextIcon}
        iconColor="text-[#E8612C]"
        title="Document Vault"
        subtitle="Manage contracts, blueprints, and compliance files"
        onRefresh={() => window.location.reload()}
      >
        <TopPrimaryButton
          onClick={() => navigate("/admin/DocumentPage/add")}
          label="Upload Document"
        />
      </PageHeader>

      {/* 2. KPI DASHBOARD */}
      <StatsGrid stats={stats} cols={4} />

      {/* 3. CONTROL BAR */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search filenames or tags..."
      />

      {/* 4. SPLIT PANE LAYOUT */}
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 overflow-hidden">
          
          {/* Left: Document List */}
          <div className={`flex-1 bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] overflow-hidden flex flex-col min-h-0 transition-all duration-300 ${selectedDoc ? 'hidden lg:flex' : 'flex'}`}>
            <DocumentListView searchTerm={searchTerm} filterProject={filterProject} onSelect={setSelectedDoc} />
          </div>

          {/* Right: Slide-in Detail Panel */}
          {selectedDoc && (
              <div className="w-full lg:w-[400px] xl:w-[450px] bg-white dark:bg-[#161b22] rounded-xl shadow-lg border border-gray-200 dark:border-[#30363d] flex flex-col overflow-hidden animate-slide-in-right shrink-0 z-10 h-[calc(100vh-250px)]">
                  <div className="bg-gray-50 dark:bg-[#21262d] px-4 py-3 border-b border-gray-200 dark:border-[#30363d] flex justify-between items-center shrink-0">
                      <h3 className="font-bold text-gray-800 dark:text-white text-sm"><T>Document Preview</T></h3>
                      <button onClick={() => setSelectedDoc(null)} className="text-gray-400 hover:text-gray-800 dark:hover:text-white transition"><X size={16} /></button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {/* Fake PDF Placeholder for UI Polish */}
                      <div className="w-full h-48 bg-gray-100 dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] rounded flex flex-col items-center justify-center text-gray-400 dark:text-slate-500">
                          <FileText size={48} className="mb-2 text-gray-300 dark:text-slate-600" />
                          <span className="text-xs font-bold uppercase">No Preview Available</span>
                      </div>

                      <div className="border-b border-gray-100 dark:border-[#30363d] pb-3">
                          <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">{selectedDoc.name || 'Untitled Document.pdf'}</h2>
                          <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-1 uppercase font-bold tracking-wider">Uploaded: {selectedDoc.date || new Date().toLocaleDateString()}</p>
                      </div>

                      <div className="space-y-2">
                          <h4 className="text-[10px] font-bold uppercase text-gray-400 dark:text-slate-500 tracking-wider mb-1"><T>Metadata</T></h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="bg-gray-50 dark:bg-[#21262d] border border-gray-100 dark:border-[#30363d] p-2 rounded">
                                  <p className="text-[9px] text-gray-400 dark:text-slate-500 font-bold uppercase mb-0.5"><T>Project</T></p>
                                  <p className="font-bold text-gray-800 dark:text-slate-200 truncate">{selectedDoc.project || 'General'}</p>
                              </div>
                              <div className="bg-gray-50 dark:bg-[#21262d] border border-gray-100 dark:border-[#30363d] p-2 rounded">
                                  <p className="text-[9px] text-gray-400 dark:text-slate-500 font-bold uppercase mb-0.5"><T>Type</T></p>
                                  <p className="font-bold text-gray-800 dark:text-slate-200">{selectedDoc.type || 'PDF'}</p>
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-[#21262d] px-4 py-3 border-t border-gray-200 dark:border-[#30363d] flex justify-end gap-2 shrink-0">
                      <button onClick={() => setSelectedDoc(null)} className="px-4 py-1.5 bg-white dark:bg-[#30363d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-200 rounded text-xs font-bold hover:bg-gray-100 dark:hover:bg-[#1E293B] transition">Close</button>
                      <button className="px-4 py-1.5 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700 transition flex items-center gap-1 shadow-sm"><Download size={12}/> Download File</button>
                  </div>
              </div>
          )}
      </div>
    </PageLayout>
  );
};

export default DocumentPage;